import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, BrandM } from '@sorare/core/src/atoms/typography';
import { HideSeriesBottomNav } from '@sorare/core/src/components/navigation/AppNavigation/HideSeriesBottomNav';
import {
  ANY_SPORT_SERIES_SQUAD_CREATE,
  ANY_SPORT_SERIES_SQUAD_JOIN,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';
import { useCommonGameSet } from '@sorare/core/src/set';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import { SeriesBoardHeader } from '#src/components/series/SeriesBoard/SeriesBoardHeader/index.tsx';
import { SquadBackground } from '#src/components/squad/SquadBackground/index.tsx';
import { SquadSearch } from '#src/components/squad/SquadSearch/index.tsx';
import { useEnablePublicSquads } from '#src/hooks/squad/useEnablePublicSquads.ts';

const Root = styled(Vertical)`
  position: relative;
  height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
  isolation: isolate;
`;

const Illustration = styled.img`
  flex: 4;
  min-height: 0;
  width: 100%;
  object-fit: contain;
`;

const TopFade = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 230px;
  background: linear-gradient(to bottom, var(--c-black) 25%, transparent);
  z-index: 0;
`;

const StyledContainer = styled(Container)`
  backdrop-filter: blur(4px);
  @media ${tabletAndAbove} {
    backdrop-filter: none;
  }
`;

const Spacer = styled.div`
  flex: 1;
  min-height: 0;
`;

const Wrapper = styled(Horizontal).attrs({ center: true })`
  flex: 2;
`;

const BottomCard = styled(Vertical)`
  position: relative;
  padding-bottom: var(--triple-unit);
`;

const CTARow = styled(Horizontal)`
  width: 100%;
  > * {
    flex: 1;
  }
`;

const publicRoot = css`
  position: relative;
  min-height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
  isolation: isolate;
`;

const publicIllustration = css`
  width: 100%;
  max-height: 33vh;
  object-fit: contain;
`;

export const SquadCreateOrJoin = () => {
  const { generateSportContextPath } = useSportContext();
  const { visual } = useCommonGameSet();
  const enablePublicSquads = useEnablePublicSquads();
  const teamLogos = visual.squad?.teamLogos;

  if (enablePublicSquads) {
    return (
      <Vertical gap={3} className={publicRoot}>
        <TopFade />
        <SeriesBoardHeader squad={null} selectedTrack="squad" />
        <SquadBackground />
        {teamLogos ? (
          <img src={teamLogos} alt="" className={publicIllustration} />
        ) : null}
        <Vertical gap={3} center className="relative text-center">
          <Vertical gap={0.5} center>
            <BrandM>
              <FormattedMessage defaultMessage="Build your legacy" />
            </BrandM>
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Invite, play and compete with your friends" />
            </BodyS>
          </Vertical>
          <Button
            size="medium"
            color="primary"
            to={generateSportContextPath(ANY_SPORT_SERIES_SQUAD_CREATE)}
          >
            <FormattedMessage defaultMessage="Create squad" />
          </Button>
        </Vertical>
        <StyledContainer maxWidth={600} className="relative pb-3">
          <SquadSearch />
        </StyledContainer>
        <HideSeriesBottomNav />
      </Vertical>
    );
  }

  return (
    <Root>
      <TopFade />
      <SeriesBoardHeader squad={null} selectedTrack="squad" />
      <SquadBackground />
      <Spacer />
      {teamLogos ? <Illustration src={teamLogos} alt="" /> : null}
      <Wrapper>
        <Vertical gap={0.5} center className="text-center">
          <BrandM>
            <FormattedMessage defaultMessage="Join or create a squad" />
          </BrandM>
          <BodyM color="var(--c-nd-700)">
            <FormattedMessage defaultMessage="Invite, play and compete with your friends" />
          </BodyM>
        </Vertical>
      </Wrapper>
      <StyledContainer maxWidth={600}>
        <BottomCard>
          <CTARow gap={2}>
            <Button
              size="large"
              color="secondary"
              to={generateSportContextPath(ANY_SPORT_SERIES_SQUAD_JOIN)}
            >
              <FormattedMessage {...glossary.join} />
            </Button>
            <Button
              size="large"
              color="primary"
              to={generateSportContextPath(ANY_SPORT_SERIES_SQUAD_CREATE)}
            >
              <FormattedMessage {...glossary.create} />
            </Button>
          </CTARow>
        </BottomCard>
      </StyledContainer>
      <HideSeriesBottomNav />
    </Root>
  );
};
